import React, {useState, useEffect} from "react";
import Select from "react-select";
import DatePicker from "react-datepicker";

import "./ProblemSearch.css";
import {StandardButton} from "../../../common/button/standard-button/StandardButton";
import {
    retrieveCountriesForDropdown,
    retrieveDistrictsForDropdown, retrieveFacilitiesForDropdown,
    retrieveProvincesForDropdown, retrieveSubDistrictsForDropdown
} from "../../../../services/FacilityService";
import {retrieveAllTopicAreas} from "../../../../services/TopicAreaService";
import {retrieveAllPersons} from "../../../../services/PersonService";
import {createPersonDropdownDisplayValue, sortPersonList} from "../../../../util/PersonUtil";
import {retrieveAllGrants} from "../../../../services/GrantService";
import {Toggle} from "../../../common/toggle/Toggle";

export const ProblemSearch = ({context, setContext, navigateTo, setSpinnerText}) => {

    const [searchLocationByDropdown, setSearchLocationByDropdown] = useState(true);

    const [country, setCountry] = useState(null);
    const [province, setProvince] = useState(null);
    const [district, setDistrict] = useState(null);
    const [subDistrict, setSubDistrict] = useState(null);
    const [facility, setFacility] = useState(null);

    const [countryText, setCountryText] = useState(null);
    const [provinceText, setProvinceText] = useState(null);
    const [districtText, setDistrictText] = useState(null);
    const [subDistrictText, setSubDistrictText] = useState(null);
    const [facilityText, setFacilityText] = useState(null);

    const [dateOfVisitFrom, setDateOfVisitFrom] = useState(null);
    const [dateOfVisitTo, setDateOfVisitTo] = useState(null);
    const [topicArea, setTopicArea] = useState(null);
    const [associatedPerson, setAssociatedPerson] = useState(null);
    const [problemStatement, setProblemStatement] = useState(null);
    const [problemStatus, setProblemStatus] = useState(null);
    const [grantName, setGrantName] = useState(null);

    const [countryList, setCountryList] = useState(null);
    const [provinceList, setProvinceList] = useState(null);
    const [districtList, setDistrictList] = useState(null);
    const [subDistrictList, setSubDistrictList] = useState(null);
    const [facilityList, setFacilityList] = useState(null);
    const [topicAreaList, setTopicAreaList] = useState(null);
    const [associatedPersonList, setAssociatedPersonList] = useState(null);
    const [problemStatusList, setProblemStatusList] = useState(null);
    const [grantList, setGrantList] = useState(null);

    useEffect(() => {
        setSpinnerText("Retrieving Countries");
        const fetchData = async () => {
            const [data] = await retrieveCountriesForDropdown(context.username);
            if (data) {
                if (data.responseStatus === "Success") {
                    const countryList = [];
                    const resultMap = data.resultMap;
                    for (const property in resultMap) {
                        countryList.push({value: property, label: resultMap[property]});
                    }
                    setCountryList(countryList);
                }
            }
            setSpinnerText(null);
        }
        fetchData();
    }, []);

    useEffect(() => {
        if (country) {
            setSpinnerText("Retrieving Provinces");
            const fetchData = async () => {
                const [data] = await retrieveProvincesForDropdown(context.username, country.label);
                if (data) {
                    if (data.responseStatus === "Success") {
                        const provinceList = [];
                        const resultMap = data.resultMap;
                        for (const property in resultMap) {
                            provinceList.push({value: property, label: resultMap[property]});
                        }
                        setProvinceList(provinceList);
                    }
                }
                setSpinnerText(null);
            }
            fetchData();
        }
    }, [country]);

    useEffect(() => {
        if (province) {
            setSpinnerText("Retrieving Districts");
            const fetchData = async () => {
                const [data] = await retrieveDistrictsForDropdown(context.username, country.label, province.label);
                if (data) {
                    if (data.responseStatus === "Success") {
                        const districtList = [];
                        const resultMap = data.resultMap;
                        for (const property in resultMap) {
                            districtList.push({value: property, label: resultMap[property]});
                        }
                        setDistrictList(districtList);
                    }
                }
                setSpinnerText(null);
            }
            fetchData();
        }
    }, [province]);

    useEffect(() => {
        if (district) {
            setSpinnerText("Retrieving Sub Districts");
            const fetchData = async () => {
                const [data] = await retrieveSubDistrictsForDropdown(context.username, country.label, province.label, district.label);
                if (data) {
                    if (data.responseStatus === "Success") {
                        const subDistrictList = [];
                        const resultMap = data.resultMap;
                        for (const property in resultMap) {
                            subDistrictList.push({value: property, label: resultMap[property]});
                        }
                        setSubDistrictList(subDistrictList);
                    }
                }
                setSpinnerText(null);
            }
            fetchData();
        }
    }, [district]);

    useEffect(() => {
        if (subDistrict) {
            setSpinnerText("Retrieving Facilities");
            const fetchData = async () => {
                const [data] = await retrieveFacilitiesForDropdown(context.username, country.label, province.label, district.label, subDistrict.label);
                if (data) {
                    if (data.responseStatus === "Success") {
                        const facilityList = [];
                        const resultMap = data.resultMap;
                        for (const property in resultMap) {
                            facilityList.push({value: property, label: resultMap[property]});
                        }
                        setFacilityList(facilityList);
                    }
                }
                setSpinnerText(null);
            }
            fetchData();
        }
    }, [subDistrict]);

    useEffect(() => {
        const fetchTopicAreas = async () => {
            const [data] = await retrieveAllTopicAreas(context.username);
            if (data) {
                if (data.responseStatus === "Success") {
                    const activeTopicAreas = data.topicAreaVOList.filter((topicArea) => topicArea.active);
                    setTopicAreaList(activeTopicAreas);
                }
            }
        }
        fetchTopicAreas();
    }, []);

    useEffect(() => {
        const fetchPersons = async () => {
            setSpinnerText("Retrieving All Persons");
            const [data] = await retrieveAllPersons(context.username);
            if (data) {
                if (data.responseStatus === "Success") {
                    const mappedPersons = data.personVOList.map((person) => {
                        return {...person, displayValue: createPersonDropdownDisplayValue(person)};
                    });
                    sortPersonList(mappedPersons);
                    setAssociatedPersonList(mappedPersons);
                }
            }
            setSpinnerText(null);
        };
        fetchPersons();
    }, []);

    useEffect(() => {
        setProblemStatusList([
            {value: 1, label: "Abandon"},
            {value: 2, label: "Adopt"},
            {value: 3, label: "Adapt"},
            {value: 4, label: "Scale Up"},
            {value: 5, label: "Test Under Different Conditions"},
        ]);
    }, []);

    useEffect(() => {
        const fetchGrants = async () => {
            setSpinnerText("Retrieving Grants ");
            const [data] = await retrieveAllGrants(context.username);
            if (data) {
                if (data.responseStatus === "Success") {
                    const filteredGrants = data.grantVOS.filter(grant => grant.active);
                    const mappedGrants = filteredGrants.map((grant) => {
                        return {value: grant.externalReference, label: grant.name};
                    });
                    setGrantList(mappedGrants);
                }
            }
            setSpinnerText(null);
        };
        fetchGrants();
    }, []);

    const handleSearchClick = () => {
        let problemSearchInput = {
            country: (searchLocationByDropdown) ? country : {label: countryText},
            province: (searchLocationByDropdown) ? province : {label: provinceText},
            district: (searchLocationByDropdown) ? district : {label: districtText},
            subDistrict: (searchLocationByDropdown) ? subDistrict : {label: subDistrictText},
            facility: (searchLocationByDropdown) ? facility : {label: facilityText},
            dateOfVisitFrom,
            dateOfVisitTo,
            topicArea,
            associatedPerson,
            problemStatement,
            problemStatus: problemStatus ? problemStatus.label : null,
            grantName: grantName ? grantName.label : null
        };
        setContext((prevState) => ({
            ...prevState, problemSearchInput
        }));
        navigateTo("problemSearchResults");
    }

    return (
        <div className="problem-search-container">
            <div className="location-search-container">
                <div className="location-search-header-container">
                    <h3 className="problem-five-header">Location Search</h3>
                    <div className="problem-five-toggle-container">
                        <p className="problem-five-toggle-text">{(searchLocationByDropdown) ? "Search by Dropdown" : "Search by Text"}</p>
                        <Toggle active={searchLocationByDropdown} setActive={setSearchLocationByDropdown}/>
                    </div>
                </div>
                {searchLocationByDropdown && <div>
                    <div className="problem-search-input-container">
                        <label>Country:</label>
                        <div className="problem-search-select-container">
                            <Select options={countryList}
                                    onChange={selectedCountry => setCountry(selectedCountry)}
                                    isDisabled={!countryList}
                                    value={country}
                                    classNamePrefix="select"
                                    isClearable
                            />
                        </div>
                    </div>
                    <div className="problem-search-input-container">
                        <label>Province:</label>
                        <div className="problem-search-select-container">
                            <Select options={provinceList}
                                    onChange={selectedProvince => setProvince(selectedProvince)}
                                    isDisabled={!provinceList}
                                    value={province}
                                    classNamePrefix="select"
                                    isClearable
                            />
                        </div>
                    </div>
                    <div className="problem-search-input-container">
                        <label>District:</label>
                        <div className="problem-search-select-container">
                            <Select options={districtList}
                                    onChange={selectedDistrict => setDistrict(selectedDistrict)}
                                    isDisabled={!districtList}
                                    value={district}
                                    classNamePrefix="select"
                                    isClearable
                            />
                        </div>
                    </div>
                    <div className="problem-search-input-container">
                        <label>Sub-District:</label>
                        <div className="problem-search-select-container">
                            <Select options={subDistrictList}
                                    onChange={selectedSubDistrict => setSubDistrict(selectedSubDistrict)}
                                    isDisabled={!subDistrictList}
                                    value={subDistrict}
                                    classNamePrefix="select"
                                    isClearable
                            />
                        </div>
                    </div>
                    <div className="problem-search-input-container">
                        <label>Facility:</label>
                        <div className="problem-search-select-container">
                            <Select options={facilityList}
                                    onChange={selectedFacility => setFacility(selectedFacility)}
                                    isDisabled={!facilityList}
                                    value={facility}
                                    classNamePrefix="select"
                                    isClearable
                            />
                        </div>
                    </div>
                </div>}
                {!searchLocationByDropdown && <div>
                    <div className="problem-search-input-container">
                        <label>Country:</label>
                        <div className="problem-search-select-container">
                            <input className="problem-search-text-input" onChange={({target}) => {
                                setCountryText(target.value);
                            }}/>
                        </div>
                    </div>
                    <div className="problem-search-input-container">
                        <label>Province:</label>
                        <div className="problem-search-select-container">
                            <input className="problem-search-text-input" onChange={({target}) => {
                                setProvinceText(target.value);
                            }}/>
                        </div>
                    </div>
                    <div className="problem-search-input-container">
                        <label>District:</label>
                        <div className="problem-search-select-container">
                            <input className="problem-search-text-input" onChange={({target}) => {
                                setDistrictText(target.value);
                            }}/>
                        </div>
                    </div>
                    <div className="problem-search-input-container">
                        <label>Sub-District:</label>
                        <div className="problem-search-select-container">
                            <input className="problem-search-text-input" onChange={({target}) => {
                                setSubDistrictText(target.value);
                            }}/>
                        </div>
                    </div>
                    <div className="problem-search-input-container">
                        <label>Facility:</label>
                        <div className="problem-search-select-container">
                            <input className="problem-search-text-input" onChange={({target}) => {
                                setFacilityText(target.value);
                            }}/>
                        </div>
                    </div>
                </div>}
            </div>
            <div className="problem-search-input-container">
                <label>Date of Visit:</label>
                <div className="problem-search-date-input-container">
                    <p className="problem-search-date-input-from">From:</p>
                    <DatePicker selected={dateOfVisitFrom} onChange={setDateOfVisitFrom} className="text-input"
                                showPopperArrow={false}
                    />
                    <p className="problem-search-date-input-to">To:</p>
                    <DatePicker selected={dateOfVisitTo} onChange={setDateOfVisitTo} className="text-input"
                                showPopperArrow={false}
                    />
                </div>
            </div>
            <div className="problem-search-input-container">
                <label>Topic Area:</label>
                <div className="problem-search-select-container">
                    <Select options={topicAreaList}
                            onChange={selectedTopicArea => setTopicArea(selectedTopicArea)}
                            getOptionLabel={(topicArea) => topicArea.topicArea}
                            getOptionValue={(topicArea) => topicArea.externalReference}
                            isDisabled={!topicAreaList}
                            value={topicArea}
                            classNamePrefix="select"
                            isClearable
                    />
                </div>
            </div>
            <div className="problem-search-input-container">
                <label>Associated Person:</label>
                <div className="problem-search-select-container">
                    <Select options={associatedPersonList}
                            onChange={selectedPerson => setAssociatedPerson(selectedPerson)}
                            getOptionLabel={(availableContactPerson) => availableContactPerson.displayValue}
                            getOptionValue={(availableContactPerson) => availableContactPerson.externalReference}
                            isDisabled={!associatedPersonList}
                            value={associatedPerson}
                            classNamePrefix="select"
                            isClearable
                    />
                </div>
            </div>
            <div className="problem-search-input-container">
                <label>Problem Statement:</label>
                <div className="problem-search-select-container">
                    <input className="text-input" onChange={({target}) => {
                        setProblemStatement(target.value);
                    }}/>
                </div>
            </div>
            <div className="problem-search-input-container">
                <label>Problem Status:</label>
                <div className="problem-search-select-container">
                    <Select options={problemStatusList}
                            onChange={selectedStatus => setProblemStatus(selectedStatus)}
                            isDisabled={!problemStatusList}
                            value={problemStatus}
                            classNamePrefix="select"
                            isClearable
                    />
                </div>
            </div>
            <div className="problem-search-input-container">
                <label>Grant:</label>
                <div className="problem-search-select-container">
                    <Select options={grantList}
                            onChange={selectedGrant => setGrantName(selectedGrant)}
                            isDisabled={!grantList}
                            value={grantName}
                            classNamePrefix="select"
                            isClearable
                    />
                </div>
            </div>

            <div className="problem-search-button-container">
                <StandardButton text="Search" onClick={handleSearchClick}/>
            </div>
        </div>
    );
}
